import { BIOMES } from './TerrainConstants.js';

/**
 * Manages biome data and provides utility functions for biome operations
 * Responsible for:
 * - Storing and managing biome definitions
 * - Providing biome-specific utilities
 * - Managing biome transitions and features
 */
export class BiomeManager {
    constructor(biomeGenerator) {
        this.biomeGenerator = biomeGenerator;
        this.biomes = this.setupBiomes();
        console.log('[BiomeManager] Initialized with biome definitions');
    }
    
    /**
     * Sets up biome definitions with their properties and features
     * @returns {Object} Biome definitions
     */
    setupBiomes() {
        return {
            [BIOMES.DEEP_OCEAN]: {
                name: 'Deep Ocean',
                topBlock: 'sand',
                underBlock: 'stone',
                features: {}
            },
            [BIOMES.OCEAN]: {
                name: 'Ocean',
                topBlock: 'sand',
                underBlock: 'stone',
                features: {}
            },
            [BIOMES.SHORE]: {
                name: 'Shore',
                topBlock: 'sand',
                underBlock: 'sandstone',
                features: {}
            },
            [BIOMES.PLAINS]: {
                name: 'Plains',
                topBlock: 'grass',
                underBlock: 'dirt',
                features: {
                    tree: {
                        chance: 0.01,
                        height: {min: 4, max: 6}
                    },
                    tallGrass: {
                        chance: 0.1
                    }
                }
            },
            [BIOMES.HILLS]: {
                name: 'Hills',
                topBlock: 'grass',
                underBlock: 'dirt',
                features: {
                    tree: {
                        chance: 0.03,
                        height: {min: 4, max: 7}
                    }
                }
            },
            [BIOMES.MOUNTAINS]: {
                name: 'Mountains',
                topBlock: 'stone',
                underBlock: 'stone',
                features: {}
            }
        };
    }
    
    /**
     * Gets biome data at a specific world position
     * @param {number} x - World X coordinate
     * @param {number} z - World Z coordinate
     * @returns {Object} Biome data
     */
    getBiomeAt(x, z) {
        const biomeType = this.biomeGenerator.getBiome(x, z);
        return this.biomes[biomeType] || this.biomes[BIOMES.PLAINS]; // Fallback to plains
    }

    /**
     * Gets the biome type at a specific world position
     * @param {number} x - World X coordinate
     * @param {number} z - World Z coordinate
     * @returns {string} Biome type
     */
    getBiomeType(x, z) {
        return this.biomeGenerator.getBiome(x, z);
    }

    /**
     * Checks if a feature should be generated at a position
     * @param {string} biomeType - Type of biome
     * @param {string} featureType - Type of feature
     * @param {number} x - World X coordinate
     * @param {number} z - World Z coordinate
     * @returns {boolean} Whether to generate the feature
     */
    shouldGenerateFeature(biomeType, featureType, x, z) {
        const biome = this.biomes[biomeType];
        if (!biome?.features?.[featureType]) return false;

        const feature = biome.features[featureType];
        const biomeData = this.biomeGenerator.getBiomeData(x, z);
        const noise = biomeData.featureNoise;
        return noise < feature.chance;
    }

    /**
     * Gets feature parameters for a specific biome and feature type
     * @param {string} biomeType - Type of biome
     * @param {string} featureType - Type of feature
     * @returns {Object|null} Feature parameters or null if not found
     */
    getFeatureParams(biomeType, featureType) {
        return this.biomes[biomeType]?.features?.[featureType] || null;
    }

    /**
     * Gets the appropriate block type for a biome at a specific depth
     * @param {string} biomeType - Type of biome
     * @param {number} depth - Depth below surface
     * @returns {string} Block type
     */
    getBlockType(biomeType, depth) {
        const biome = this.biomes[biomeType];
        if (!biome) return 'stone';

        if (depth === 0) return biome.topBlock;
        if (depth === 1) return biome.underBlock;
        return 'stone';
    }
}
